import type { IExistContact } from "@api";
import type { IContact } from "@bbj-types/contact";
import { ContactCardCreateErrors } from "@candidate/components/ContactCreationError/errors";
import { AutocompletePlaces } from "@inputs/autocomplete/AutocompletePlacesUniq";
import { Stack, TextField, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import type React from "react";
import { StackGrid } from "../../data-display/layout/StackGrid";
import type { ContactFormError } from "./logics";
import type { ContactPickerEnterprise } from "./types";

interface IContactPickerCreateProps {
  contact: IContact | null;
  enterprise: ContactPickerEnterprise | null;
  alreadyExistsContact: IExistContact;
  setContact: React.Dispatch<React.SetStateAction<IContact | null>>;
  hidden: boolean;
  error: ContactFormError;
}

export const ContactPickerCreate = ({
  contact,
  enterprise,
  alreadyExistsContact,
  setContact,
  hidden,
  error,
}: IContactPickerCreateProps) => {
  const { user } = useGlobalContext();
  const { enterpriseId } = alreadyExistsContact || {};

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        display: hidden ? "none" : "unset",
      }}
    >
      <StackGrid rowGap={1} columnGap={1} perLine={2}>
        <TextField
          label="Prénom"
          size="small"
          fullWidth
          value={contact?.firstName || ""}
          onChange={(evt) => setContact((contact) => contact && { ...contact, firstName: evt.target.value })}
        />
        <TextField
          label="Nom"
          size="small"
          fullWidth
          value={contact?.lastName || ""}
          onChange={(evt) => setContact((contact) => contact && { ...contact, lastName: evt.target.value })}
        />
        <TextField
          label="Poste"
          size="small"
          fullWidth
          value={contact?.job || ""}
          onChange={(evt) => setContact((contact) => contact && { ...contact, job: evt.target.value })}
        />
        <TextField
          label="Email"
          size="small"
          fullWidth
          value={contact?.email || ""}
          error={error.contactInformation}
          onChange={(evt) => setContact((contact) => contact && { ...contact, email: evt.target.value })}
        />
        <TextField
          label="Téléphone"
          size="small"
          fullWidth
          value={contact?.phone || ""}
          error={error.contactInformation}
          onChange={(evt) => setContact((contact) => contact && { ...contact, phone: evt.target.value })}
        />
        <AutocompletePlaces
          value={contact?.place || user.organisation.places}
          onChange={(_, value) => {
            if (contact && value) {
              setContact({ ...contact, place: value });
            } else if (contact && value === null) {
              setContact({
                ...contact,
                place: { _id: "", placeLabel: "", postalCodes: [], placeKind: "" },
              });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Zone géographique*" size="small" fullWidth error={error.place} />
          )}
          multiple={false}
        />
      </StackGrid>
      <Typography component="p" variant="caption">
        Donnez le maximum d'informations sur ce contact : quelle est la nature de la relation ? comment transmettre les
        candidatures ? etc.
      </Typography>
      <TextField
        label="Commentaires"
        size="small"
        multiline
        rows={3}
        fullWidth
        value={contact?.description || ""}
        onChange={(evt) => setContact((contact) => contact && { ...contact, description: evt.target.value })}
      />
      <Typography component="p" variant="caption">
        Pour un contact, il faut a minima un email ou un téléphone.
      </Typography>
      {alreadyExistsContact.contact && (
        <ContactCardCreateErrors
          existsContact={alreadyExistsContact}
          existsInEnterprise={enterpriseId === enterprise?._id}
        />
      )}
    </Stack>
  );
};
